import { Alert, Button, Card, Descriptions, Form, Input, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationApplicationSelect,
  EducationGradeSelect,
  EducationSessionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type EligibleSection = {
  id: string;
  name: string;
  capacity: number;
  enrolled_count: number;
  seats_available: number;
};

type Enrollment = {
  id: string;
  enrollment_no: string;
  status: string;
  record_version: number;
  student_id: string;
  session_id?: string;
  grade_level_id?: string;
  effective_on?: string | null;
  student?: { admission_no?: string; first_name?: string; last_name?: string | null };
  session?: { code?: string; name?: string };
  grade?: { code?: string; name?: string };
  current_section?: { section_id?: string; section_name?: string } | null;
  eligible_sections?: EligibleSection[];
  allocation_history?: Array<{
    id: string;
    section_name?: string;
    effective_on?: string;
    ended_on?: string | null;
    reason?: string | null;
    is_current?: number | boolean;
  }>;
  can_allocate?: boolean;
  can_transfer?: boolean;
  allocate_blocked_reason?: string | null;
  transfer_blocked_reason?: string | null;
};

export default function EnrollmentDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const back = `/education/enrollments?${params.toString()}`;
  const isNew = !id || id === 'new';
  const query = useQuery({
    queryKey: ['education-enrollment', id],
    enabled: !isNew,
    queryFn: () => api.get<Enrollment>(`/education/enrollments/${id}`),
  });
  const create = useMutation({
    mutationFn: (values: Record<string, string>) => api.post<{ id: string }>('/education/enrollments', values),
    onSuccess: (result) => navigate(`/education/enrollments/${result.data?.id}?${params.toString()}`),
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  const enrollment = query.data?.data;
  const confirm = useMutation({
    mutationFn: () => api.post(`/education/enrollments/${id}/confirm`, { record_version: enrollment?.record_version }),
    onSuccess: () => {
      message.success(t('confirmSubmitted', { defaultValue: 'Submitted for confirmation' }));
      queryClient.invalidateQueries({ queryKey: ['education-enrollment', id] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const studentName = [enrollment?.student?.first_name, enrollment?.student?.last_name].filter(Boolean).join(' ');
  const sections = enrollment?.eligible_sections ?? [];

  return (
    <EducationPageChrome
      title={t('enrollmentTitle')}
      description={t('enrollmentsIntro')}
      tags={enrollment ? <Tag>{enrollment.status}</Tag> : undefined}
      extra={<Button onClick={() => navigate(back)}>{t('backToEnrollments')}</Button>}
    >
      <Card size="small" loading={!isNew && query.isLoading}>
        {isNew ? (
          <Form
            layout="vertical"
            initialValues={{ student_id: params.get('student_id') || undefined }}
            onFinish={(values) => create.mutate(values)}
            style={{ maxWidth: 520 }}
          >
            <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
              <EducationStudentSelect />
            </Form.Item>
            <Form.Item name="application_id" label={t('application')}>
              <EducationApplicationSelect />
            </Form.Item>
            <Form.Item name="session_id" label={t('session')}>
              <EducationSessionSelect />
            </Form.Item>
            <Form.Item name="grade_level_id" label={t('grade')}>
              <EducationGradeSelect />
            </Form.Item>
            <Form.Item
              name="enrollment_no"
              label={t('enrollmentNo')}
              extra={t('enrollmentNoAutoHint', { defaultValue: 'Leave blank to auto-generate' })}
            >
              <Input placeholder={t('autoGenerated', { defaultValue: 'Auto-generated if blank' })} />
            </Form.Item>
            <Form.Item name="effective_on" label={t('effectiveOn')}>
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              {t('save')}
            </Button>
          </Form>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
              <Descriptions.Item label={t('enrollmentNo')}>{enrollment?.enrollment_no || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                {enrollment?.status ? <Tag>{enrollment.status}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('student')}>
                {studentName || '—'}
                {enrollment?.student?.admission_no ? ` (${enrollment.student.admission_no})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label={t('session')}>
                {enrollment?.session?.name || enrollment?.session?.code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('grade')}>
                {enrollment?.grade?.name || enrollment?.grade?.code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('currentSection', { defaultValue: 'Current section' })}>
                {enrollment?.current_section?.section_name || t('notAllocated', { defaultValue: 'Not allocated' })}
              </Descriptions.Item>
              <Descriptions.Item label={t('effectiveOn')}>{enrollment?.effective_on || '—'}</Descriptions.Item>
            </Descriptions>

            {!enrollment?.can_allocate && enrollment?.allocate_blocked_reason ? (
              <Alert type="info" showIcon message={t('allocateSection')} description={enrollment.allocate_blocked_reason} />
            ) : null}
            {!enrollment?.can_transfer && enrollment?.transfer_blocked_reason && enrollment?.current_section ? (
              <Alert type="info" showIcon message={t('transferSection')} description={enrollment.transfer_blocked_reason} />
            ) : null}

            {(sections.length > 0 || enrollment?.session_id) && (
              <Card
                size="small"
                type="inner"
                title={t('eligibleSections', { defaultValue: 'Eligible sections (this grade & session)' })}
                extra={
                  enrollment?.session_id ? (
                    <Button size="small" onClick={() => navigate(`/education/academic/sessions/${enrollment.session_id}?panel=sections`)}>
                      {t('manageSections', { defaultValue: 'Manage sections' })}
                    </Button>
                  ) : null
                }
              >
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={sections}
                  locale={{
                    emptyText: t('noEligibleSections', {
                      defaultValue: 'No matching sections — create them under Academic Sessions for this grade',
                    }),
                  }}
                  columns={[
                    { title: t('section'), dataIndex: 'name' },
                    { title: t('capacity'), dataIndex: 'capacity', width: 90 },
                    {
                      title: t('enrolled', { defaultValue: 'Enrolled' }),
                      dataIndex: 'enrolled_count',
                      width: 90,
                    },
                    {
                      title: t('seatsLeft', { defaultValue: 'Seats left' }),
                      dataIndex: 'seats_available',
                      width: 100,
                      render: (v: number) => <Tag color={v > 0 ? 'green' : 'red'}>{v}</Tag>,
                    },
                  ]}
                />
              </Card>
            )}

            {(enrollment?.allocation_history?.length ?? 0) > 0 ? (
              <Card size="small" type="inner" title={t('allocationHistory', { defaultValue: 'Allocation history' })}>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={enrollment?.allocation_history}
                  columns={[
                    { title: t('section'), dataIndex: 'section_name' },
                    { title: t('effectiveOn'), dataIndex: 'effective_on', width: 130 },
                    { title: t('endedOn', { defaultValue: 'Ended on' }), dataIndex: 'ended_on', width: 130 },
                    { title: t('reason'), dataIndex: 'reason', ellipsis: true },
                    {
                      title: t('status'),
                      dataIndex: 'is_current',
                      width: 90,
                      render: (v: number | boolean) =>
                        v ? (
                          <Tag color="green">{t('current', { defaultValue: 'Current' })}</Tag>
                        ) : (
                          <Tag>{t('past', { defaultValue: 'Past' })}</Tag>
                        ),
                    },
                  ]}
                />
              </Card>
            ) : null}

            <Space wrap>
              <Button
                type="primary"
                disabled={!enrollment?.can_allocate}
                onClick={() => navigate(`/education/enrollments/${id}/allocate?${params.toString()}`)}
              >
                {t('allocateSection')}
              </Button>
              <Button
                disabled={!enrollment?.can_transfer}
                onClick={() => navigate(`/education/enrollments/${id}/transfer?${params.toString()}`)}
              >
                {t('transferSection')}
              </Button>
              <Button onClick={() => navigate(`/education/enrollments/${id}/withdrawal?${params.toString()}`)}>
                {t('withdrawal')}
              </Button>
              <Button onClick={() => navigate(`/education/students/${enrollment?.student_id}/academic-history`)}>
                {t('academicHistory')}
              </Button>
              {enrollment?.status === 'draft' ? (
                <Button type="primary" onClick={() => confirm.mutate()} loading={confirm.isPending}>
                  {t('confirmEnrollment')}
                </Button>
              ) : null}
            </Space>
          </Space>
        )}
      </Card>
    </EducationPageChrome>
  );
}
